import {
    IIOPensionInputs,
    IOFormOfPayment,
} from "app/calcs/customers/IO/interfaces"
import {
    ILocal697PensionInputs,
    Local697FormOfPayment,
} from "app/calcs/customers/local697/interfaces"
import {
    INEBFPensionInputs,
    NEBFFormOfPayment,
} from "app/calcs/customers/NEBF/interfaces"
import { ISharedPensionInputs } from "app/calcs/customers/shared/interfaces"
import { IRetirementPerson, isValidPerson } from "app/redux/Profile/model"
import { UserActions } from "app/redux/User/actions"
import { theCensusInfo } from "app/services/models/PublicCensusInfo"
import {
    IPokeandPullRequest,
    IRequestedValues,
    ISheetValues,
    IWorkbookValues,
    WorkbookServiceResponse,
} from "app/services/models/Workbook"
import { WorkbookService } from "app/services/WorkbookService"
import { toYesNo } from "app/utils"
import { INosyParker, nullNosyParker } from "app/utils/nosyParker"
import * as IODAO from "app/workbookDAOs/IODAO"
import * as NEBFDAO from "app/workbookDAOs/NEBFDAO"
import { getWebServicesConfig } from "config"
import * as _ from "lodash"
import * as moment from "moment"

interface ILocal697RunCalculationsRequest {
    member: IRetirementPerson
    spouse: IRetirementPerson
    vals: ILocal697PensionInputs
    ioInputs: IIOPensionInputs
    nebfInputs: INEBFPensionInputs
    shared: ISharedPensionInputs
    when: moment.Moment
}

interface ILocal697PlanPInfo {
    ppaPre65: number
    ppaPost65: number
    mhpPre65: number
    mhpPost65: number
    benefitAllowedPre65: number
    benefitAllowedPost65: number
    netPremiumPre65: number
    netPremiumPost65: number
    cyCredit: number
}

const sheetDateFormat = "L"
const inputsSheetName = "Input"
const outputsSheetName = "Output 697"

const calculatorSheetName = "Calculator_697"
const calcSheetRange = "a1:s36"

const planPOutputsSheetName = "Output PlanP"
const planPCalculatorSheetName = "Calculator_PlanP"
const planPSheetRange = "a1:f28"

interface IFormOfPaymentEntry {
    form: Local697FormOfPayment
    range: string
}

const formOfPaymentMap = [
    { form: Local697FormOfPayment.lifeOnly, range: "c4:i43" },
    {
        form: Local697FormOfPayment.hundredPercentHusbandAndWife,
        range: "m4:s43",
    },
]

function commonPaymentExtractor(
    when: moment.Moment,
    output: ISheetValues,
    tableKey: IFormOfPaymentEntry
) {
    // find the table corresponding to the form of payment
    const table = output[tableKey.range]

    // find the row for the year we need
    let row = _.find(table, (r) => r[0] === when.year())
    if (!row) {
        row = _.first(table)
    }
    if (!row) {
        // tslint:disable-next-line:no-debugger
        debugger
        return 0
    }

    // some worksheets use 'financial' formatting which formats 0's as '-'
    if ("-" === row[6]) {
        row[6] = 0
    }
    return row[6] || 0
}

function makeMiscExtractor() {
    return (wr: WorkbookServiceResponse) => {
        const r = {
            totalLumpSum: 0,
            totalVestingService: 0,
            totalPensionCredits: 0,
        }
        if (!wr.Results) {
            return r
        }
        const calcOutputs = wr.Results[calculatorSheetName]
        const calcRange = calcOutputs[calcSheetRange]
        const findData = (what: string) =>
            _.find(calcRange, (z) => z[1] === what)
        const rr = findData("Total Lump Sum")

        if (rr) {
            const v = rr[2] as number
            r.totalLumpSum = v
            r.totalVestingService = v
        }

        const totalVestingService = findData("Total Vesting Service")
        if (totalVestingService) {
            const v = totalVestingService[2] as number
            r.totalVestingService = v
        }

        const totalPensionCredits = findData("Total Pension Credits")
        if (totalPensionCredits) {
            const v = totalPensionCredits[2] as number
            r.totalPensionCredits = v
        }

        return r
    }
}

function makePlanPMiscExtractor() {
    return (wr: WorkbookServiceResponse): ILocal697PlanPInfo => {
        const r = {
            ppaPre65: 0,
            ppaPost65: 0,
            mhpPre65: 0,
            mhpPost65: 0,
            benefitAllowedPre65: 0,
            benefitAllowedPost65: 0,
            netPremiumPre65: 0,
            netPremiumPost65: 0,
            cyCredit: 0,
        }
        if (!wr.Results) {
            return r
        }
        const calcOutputs = wr.Results[planPCalculatorSheetName]
        const calcRange = calcOutputs[planPSheetRange]
        const findRow = (what: string): any => {
            const d = _.find(calcRange, (z) => z[1] === what)
            if (d) {
                return d
            }
            throw new Error("PlanP- can not find row labeled as: " + what)
        }
        const getData = (row: any[], col: number) => {
            if (row) {
                return row[col]
            } else {
                return 0
            }
        }

        const ppa = findRow("Plan  P Amount Allowable")
        r.ppaPre65 = getData(ppa, 2)
        r.ppaPost65 = getData(ppa, 3)

        const mhp = findRow("Monthly Health Premium")
        r.mhpPre65 = 2100 //getData(mhp, 2)
        r.mhpPost65 = 550 //getData(mhp, 3)

        const mba = findRow("Less Plan P Monthly Benefit Allowance")
        r.benefitAllowedPre65 = getData(mba, 2)
        r.benefitAllowedPost65 = getData(mba, 3)

        const np = findRow("Monthly Health Premium, if elected:")
        r.netPremiumPre65 = getData(np, 2)
        r.netPremiumPost65 = getData(np, 3)

        // tslint:disable-next-line:no-console
        //console.log('planP.info: ' + JSON.stringify(r))
        return r
    }
}

function makePaymentExtractor(
    vals: ILocal697PensionInputs,
    when: moment.Moment
) {
    return (wr: WorkbookServiceResponse): number => {
        if (_.isUndefined(vals.formOfPayment)) {
            return 0
        }
        if (!wr.Results) {
            return 0
        }
        const output = wr.Results[outputsSheetName]
        const tableKey = _.find(
            formOfPaymentMap,
            (q) => q.form === vals.formOfPayment
        )
        return commonPaymentExtractor(when, output, tableKey)
    }
}

function makePlanPPaymentExtractor(
    vals: ILocal697PensionInputs,
    when: moment.Moment
) {
    return (wr: WorkbookServiceResponse): number => {
        if (_.isUndefined(vals.formOfPayment)) {
            return 0
        }
        if (!wr.Results) {
            return 0
        }
        const output = wr.Results[planPOutputsSheetName]
        const tableKey = _.find(
            formOfPaymentMap,
            (q) => q.form === vals.formOfPayment
        )
        return commonPaymentExtractor(when, output, tableKey)
    }
}

type Local697WorkbookDAOCalculator = (
    request: ILocal697RunCalculationsRequest
) => Promise<WorkbookServiceResponse>

class Local697WorkbookDAO {
    private service: WorkbookService

    constructor(protected nosyParker: INosyParker = nullNosyParker) {
        this.service = new WorkbookService(
            getWebServicesConfig().workbookEndpoint,
            "local697Workbook"
        )
    }

    exportExhibitTab(
        request: ILocal697RunCalculationsRequest
    ): Promise<WorkbookServiceResponse> {
        // tslint:disable-next-line:no-console
        console.log("Local697WorkbookDAO using service: " + this.service)

        this.nosyParker.operationIsStarting()
        const startTime = moment.utc()

        const inputs = this.fillRequestArgs(request)
        const outputs = {}
        const filename =
            "Local697PensionExhibit-" + moment().format("MM-YYYY") + ".pdf"
        const req: IPokeandPullRequest = { inputs, outputs, filename }

        return this.service
            .exportExhibitTab(UserActions.getSharedVar("accessToken"), req)
            .then((wr: WorkbookServiceResponse) => {
                const elapsed = moment.utc().diff(startTime)

                // tslint:disable-next-line:no-console
                console.log(
                    "It took " +
                        elapsed +
                        "ms for Local697WorkbookDAO exportExhibitTab"
                )

                if (wr.IsError || !wr.Results) {
                    // tslint:disable-next-line:no-debugger
                    debugger
                    this.nosyParker.operationHasFailed(wr)
                } else {
                    this.nosyParker.operationHasCompleted(wr)
                }

                return wr
            })
            .catch((reason) => {
                this.nosyParker.operationHasFailed(reason)
                return Promise.reject(reason)
            })
    }

    /**
     * Run the Local697 calculations workbook.
     *
     * @param request The request parameters
     *
     * @returns a WorkbookServiceResponse.
     */
    runCalculations(
        request: ILocal697RunCalculationsRequest
    ): Promise<WorkbookServiceResponse> {
        // tslint:disable-next-line:no-console
        console.log("Local697WorkbookDAO using service: " + this.service)

        this.nosyParker.operationIsStarting()
        const startTime = moment.utc()

        return this.issueRequest(request)
            .then((wr: WorkbookServiceResponse) => {
                const elapsed = moment.utc().diff(startTime)

                // tslint:disable-next-line:no-console
                console.log(
                    "It took " +
                        elapsed +
                        "ms for Local697WorkbookDAO calculations"
                )

                if (wr.IsError || !wr.Results) {
                    // tslint:disable-next-line:no-debugger
                    //debugger
                    this.nosyParker.operationHasFailed(wr)
                } else {
                    this.nosyParker.operationHasCompleted(wr)
                }

                return wr
            })
            .catch((reason) => {
                this.nosyParker.operationHasFailed(reason)
                return Promise.reject(reason)
            })
    }

    private fillRequestArgs(
        request: ILocal697RunCalculationsRequest
    ): IWorkbookValues {
        const spousePresent = isValidPerson(request.spouse)
        const spouseDOB = spousePresent
            ? request.spouse.dob.format(sheetDateFormat)
            : ""
        const spouseLifeExpectancy = spousePresent
            ? request.spouse.selectedAgeExpectancy
            : ""

        const inputs: IWorkbookValues = {
            [inputsSheetName]: {
                c4: theCensusInfo.censusDate.format(sheetDateFormat),
                "c8:c16": [
                    // General Inputs
                    [request.member.firstName],
                    [request.member.lastName],
                    [request.member.dob.format(sheetDateFormat)],
                    [request.member.gender],
                    [spouseDOB],
                    [request.member.selectedAgeExpectancy],
                    [spouseLifeExpectancy],
                    [request.shared.hoursWorked || 0],
                    [(request.vals.percentLumpSumElected || 0) + "%"],
                ],
                "c19:c22": [
                    // Local 697 Plan Inputs
                    [request.vals.beginPaymentsAge.years()],
                    [request.vals.futureServiceCredits],
                    [request.vals.vestingCredits],
                    [request.vals.monthlyBen],
                ],
                "c24:c26": [
                    // Plan P Inputs
                    [request.vals.planPPaymentsAge.years()],
                    [request.vals.planPTotalService],
                    [toYesNo(request.vals.planPEligible)],
                ],
                "c29:c30": [
                    // NEBF inputs
                    [request.nebfInputs.beginPaymentsAge.years()],
                    [request.nebfInputs.currentYearsService.asYears()],
                ],
                "c33:c36": [
                    // IO inputs
                    [request.ioInputs.beginPaymentsAge.years()],
                    [request.ioInputs.yearsOfAMembership.asYears()],
                    [toYesNo(request.ioInputs.participatesInIO)],
                    [request.vals.IOServicePost2023],
                ],
                "c38:c42": [
                    // Other Plan P
                    [request.vals.planPBen],
                    [request.vals.planPRate],
                    [request.vals.currentYearHours],
                    [request.vals.cyCredit],
                    [request.vals.planPCyCredit],
                ],
                "c44:c45": [
                    // pension and planP excess hours
                    [request.vals.pensionExcessHours],
                    [request.vals.planPExcessHours],
                ],
                "c46:c46": [[request.vals.futureServiceCredits1Max]],
            },
        }
        return inputs
    }

    private issueRequest(
        request: ILocal697RunCalculationsRequest
    ): Promise<WorkbookServiceResponse> {
        const spousePresent = isValidPerson(request.spouse)
        const spouseDOB = spousePresent
            ? request.spouse.dob.format(sheetDateFormat)
            : ""
        const spouseLifeExpectancy = spousePresent
            ? request.spouse.selectedAgeExpectancy
            : ""

        const inputs = this.fillRequestArgs(request)

        const outputs: IRequestedValues = {
            [outputsSheetName]: _.map(formOfPaymentMap, (fop) => fop.range),
            [calculatorSheetName]: [calcSheetRange],

            [planPOutputsSheetName]: _.map(
                formOfPaymentMap,
                (fop) => fop.range
            ),
            [planPCalculatorSheetName]: [planPSheetRange], // pulled for debugging purposes

            [IODAO.ioSheetName]: IODAO.ioSheetRange,
            [IODAO.ioCalculatorSheetName]: [IODAO.ioCalculatorSheetRange], // pulled for debugging purposes

            [NEBFDAO.nebfSheetName]: NEBFDAO.nebfSheetRange,
            [NEBFDAO.nebfCalculatorSheetName]: [
                NEBFDAO.nebfCalculatorSheetRange,
            ],
        }

        const req: IPokeandPullRequest = { inputs, outputs }
        return this.service.pokeAndPull(
            UserActions.getSharedVar("accessToken"),
            req
        )
    }
}

const makeNEBFPaymentExtractor = NEBFDAO.makeNEBFPaymentExtractor

export {
    ILocal697RunCalculationsRequest,
    Local697WorkbookDAO,
    Local697WorkbookDAOCalculator,
    ILocal697PlanPInfo,
    makeMiscExtractor,
    makePaymentExtractor,
    makePlanPMiscExtractor,
    makePlanPPaymentExtractor,
    makeNEBFPaymentExtractor,
}



// WEBPACK FOOTER //
// ./src/app/workbookDAOs/local697DAO/index.ts